import React from "react";
import { Box, IconButton, TextField, Tooltip, Typography } from "@material-ui/core";
import {
  Search as SearchIcon,
  KeyboardArrowUp as PrevIcon,
  KeyboardArrowDown as NextIcon,
  Close as CloseIcon,
} from "@material-ui/icons";
import { useTranslation } from "../../hooks/useTranslation";

/**
 * Caixa de busca interna do painel de transcrições.
 *
 * Comportamento (definido pelo produto):
 *   - Não filtra a lista (mantém todas as frases pelo contexto).
 *   - Realça os trechos que casam com o termo dentro de cada frase.
 *   - Mostra contador "<atual>/<total>" de matches encontrados.
 *   - Botões "anterior/próximo" para navegar entre os matches; ao mudar de
 *     match, o consumidor centraliza esse match na área visível.
 *
 * Props:
 *   - `value`         string (controlado)
 *   - `onChange`      (string) => void
 *   - `currentIndex`  number (1-based; 0 quando não há matches)
 *   - `total`         number
 *   - `onPrev`        () => void
 *   - `onNext`        () => void
 */
function TranscriptionSearchBar({
  value,
  onChange,
  currentIndex,
  total,
  onPrev,
  onNext,
}) {
  const { t } = useTranslation("common");
  const hasQuery = (value || "").trim().length > 0;

  return (
    <Box className="transcription-search">
      <Box className="transcription-search__input-wrapper">
        <SearchIcon
          fontSize="small"
          className="transcription-search__icon"
        />
        <TextField
          variant="standard"
          fullWidth
          value={value}
          placeholder={t("transcription.search.placeholder")}
          onChange={(e) => onChange?.(e.target.value)}
          InputProps={{
            disableUnderline: true,
            inputProps: {
              "aria-label": t("transcription.search.ariaLabel"),
            },
          }}
          className="transcription-search__input"
        />
        {hasQuery && (
          <Tooltip title={t("transcription.search.clear")}>
            <IconButton
              size="small"
              className="transcription-search__btn"
              onClick={() => onChange?.("")}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {hasQuery && (
        <Box className="transcription-search__nav">
          <Typography
            variant="caption"
            className="transcription-search__counter"
          >
            {total > 0
              ? t("transcription.search.counter", {
                  current: currentIndex,
                  total,
                })
              : t("transcription.search.noMatches")}
          </Typography>
          <Tooltip title={t("transcription.search.prev")}>
            <span>
              <IconButton
                size="small"
                className="transcription-search__btn"
                disabled={total === 0}
                onClick={onPrev}
              >
                <PrevIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("transcription.search.next")}>
            <span>
              <IconButton
                size="small"
                className="transcription-search__btn"
                disabled={total === 0}
                onClick={onNext}
              >
                <NextIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

export default TranscriptionSearchBar;
